/**
 * GD Live Chat Widget for Shopify
 * Version 3.0 - With Session Persistence, Fullscreen Mobile, Fixed Scroll
 */
(function () {
    'use strict';

    // ========== Configuration ==========
    const SUPABASE_URL = 'https://bsodhudnndgmpjkclhhm.supabase.co';
    const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJzb2RodWRubmRnbXBqa2NsaGhtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjcyNDc5MjYsImV4cCI6MjA4MjgyMzkyNn0.ocjKV-pnCkEfoL6ToTpnQiIDEyg9-MqIfVUQ_mKLbNk';
    const STORAGE_KEY = 'gd_live_chat_session';

    const PRODUCTS = [
        { id: 'artemis_3d', name: 'Artemis 3D', asin: 'B09YV2JG3L' },
        { id: 'ether', name: 'Ether', asin: 'B0DSW5VSX2' },
        { id: 'oxylus', name: 'Oxylus', asin: 'B07GGZWRK6' },
        { id: 'pillow', name: 'Pillow', asin: 'B0833TTF4H' },
        { id: 'apollo_air', name: 'ApolloAir', asin: 'B08D8VZPDL' },
    ];

    // ========== State ==========
    let state = {
        isOpen: false,
        step: 'form', // 'form' | 'chat'
        conversationId: null,
        customerName: '',
        customerEmail: '',
        selectedProduct: null,
        messages: [],
        supabase: null,
        channel: null,
        selectedFile: null,
        unreadCount: 0, // Track unread messages from agent
    };

    // ========== Session Storage ==========
    function saveSession() {
        const sessionData = {
            conversationId: state.conversationId,
            customerName: state.customerName,
            customerEmail: state.customerEmail,
            selectedProduct: state.selectedProduct,
            step: state.step,
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
        } catch (e) {
            console.warn('Could not save session:', e);
        }
    }

    function loadSession() {
        try {
            const data = localStorage.getItem(STORAGE_KEY);
            if (data) {
                const session = JSON.parse(data);
                if (session.conversationId) {
                    state.conversationId = session.conversationId;
                    state.customerName = session.customerName || '';
                    state.customerEmail = session.customerEmail || '';
                    state.selectedProduct = session.selectedProduct;
                    state.step = 'chat';
                    return true;
                }
            }
        } catch (e) {
            console.warn('Could not load session:', e);
        }
        return false;
    }

    function clearSession() {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) { }
        state.conversationId = null;
        state.customerName = '';
        state.customerEmail = '';
        state.selectedProduct = null;
        state.step = 'form';
        state.messages = [];
        if (state.channel) {
            state.supabase.removeChannel(state.channel);
            state.channel = null;
        }
    }

    // ========== Initialize Supabase ==========
    function initSupabase() {
        if (window.supabase) {
            state.supabase = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
        }
    }

    // ========== API Functions ==========
    async function createConversation() {
        const product = PRODUCTS.find(p => p.id === state.selectedProduct);
        if (!product) return null;

        const { data, error } = await state.supabase
            .from('live_chat_conversations')
            .insert({
                customer_email: state.customerEmail.toLowerCase().trim(),
                customer_name: state.customerName.trim(),
                asin: product.asin,
                product_name: product.name,
                status: 'open',
            })
            .select()
            .single();

        if (error) {
            console.error('Failed to create conversation:', error);
            return null;
        }
        return data;
    }

    async function sendMessage(text, attachmentUrl = null) {
        if (!state.conversationId) return;
        if (!text.trim() && !attachmentUrl) return;

        const insertData = {
            conversation_id: state.conversationId,
            sender_type: 'customer',
            message: text.trim(),
            is_read: false,
        };

        if (attachmentUrl) {
            insertData.attachment_url = attachmentUrl;
        }

        const { data, error } = await state.supabase
            .from('live_chat_messages')
            .insert(insertData)
            .select()
            .single();

        if (error) {
            console.error('Failed to send message:', error);
            return null;
        }

        // Update conversation timestamp
        await state.supabase
            .from('live_chat_conversations')
            .update({ updated_at: new Date().toISOString() })
            .eq('id', state.conversationId);

        return data;
    }

    async function uploadFile(file) {
        if (!file || !state.conversationId) return null;

        const fileExt = file.name.split('.').pop();
        const fileName = `${state.conversationId}/${Date.now()}.${fileExt}`;

        const { data, error } = await state.supabase.storage
            .from('chat-attachments')
            .upload(fileName, file);

        if (error) {
            console.error('Failed to upload file:', error);
            return null;
        }

        const { data: urlData } = state.supabase.storage
            .from('chat-attachments')
            .getPublicUrl(fileName);

        return urlData.publicUrl;
    }

    async function loadMessages() {
        if (!state.conversationId) return;

        const { data, error } = await state.supabase
            .from('live_chat_messages')
            .select('*')
            .eq('conversation_id', state.conversationId)
            .order('created_at', { ascending: true });

        if (!error && data) {
            state.messages = data;
            renderMessages();
        }
    }

    function subscribeToMessages() {
        if (!state.conversationId || !state.supabase) return;

        state.channel = state.supabase
            .channel(`chat_${state.conversationId}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'live_chat_messages',
                filter: `conversation_id=eq.${state.conversationId}`,
            }, (payload) => {
                if (!state.messages.find(m => m.id === payload.new.id)) {
                    state.messages.push(payload.new);
                    renderMessages();
                    if (payload.new.sender_type === 'agent') {
                        playNotificationSound();
                        if (!state.isOpen) {
                            state.unreadCount++;
                            updateBadge();
                            showAgentNotification();
                        }
                    }
                }
            })
            .subscribe();
    }

    function playNotificationSound() {
        try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp+fnpyam5ygpKWjoJ2bnJ2foqOioJ2am5ydoKGhn5yampudn6CfnZqYmZqdnp+dnJqYmJmanJ2dnJuZmJiZmpydnZyamZiYmZqbnJybmpmYmJmam5ybm5qZmJiZmpubnJuamZiYmZqbm5uampqZmZmam5ubm5qamZmZmpqbm5ubmpqamZmampubm5qampqamZqam5ubmpqampqampubm5ubmpqampqam5ubm5uampmam5qam5ubm5uampqampubm5ubm5qampqam5ubm5ubm5qampubm5ubm5qam5qam5ubm5ubm5ubmpubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ub');
            audio.volume = 0.3;
            audio.play().catch(() => { });
        } catch (e) { }
    }

    function updateBadge() {
        const badge = document.getElementById('lc-badge');
        if (badge) {
            if (state.unreadCount > 0 && !state.isOpen) {
                badge.textContent = state.unreadCount;
                badge.style.display = 'flex';
            } else {
                badge.style.display = 'none';
            }
        }
    }

    function showAgentNotification() {
        // Browser notification if permission granted
        if (Notification.permission === 'granted') {
            new Notification('New Message! 💬', {
                body: 'You have a new message from customer support.',
                icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23667eea"><path d="M12 3c5.5 0 10 3.58 10 8s-4.5 8-10 8c-1.24 0-2.43-.18-3.53-.5C5.55 21 2 21 2 21c2.33-2.33 2.7-3.9 2.75-4.5C3.05 15.07 2 13.13 2 11c0-4.42 4.5-8 10-8z"/></svg>'
            });
        }
    }

    // ========== UI Functions ==========
    function createWidget() {
        const style = document.createElement('style');
        style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
      
      #live-chat-widget {
        position: fixed;
        bottom: 24px;
        right: 24px;
        z-index: 99999;
        font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      /* ===== Beautiful Animated Button ===== */
      #live-chat-btn {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 14px 24px;
        border-radius: 50px;
        background: #ef4444;
        border: none;
        cursor: pointer;
        box-shadow: 0 8px 32px rgba(239, 68, 68, 0.4);
        transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        animation: float 3s ease-in-out infinite;
        position: relative;
        overflow: visible;
      }

      #live-chat-btn::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
        animation: shimmer 2s infinite;
      }

      @keyframes shimmer {
        0% { left: -100%; }
        100% { left: 100%; }
      }

      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-6px); }
      }

      #live-chat-btn:hover {
        transform: scale(1.08) translateY(-4px);
        box-shadow: 0 12px 40px rgba(239, 68, 68, 0.5);
        background: #dc2626;
      }

      #live-chat-btn svg {
        width: 24px;
        height: 24px;
        fill: #1f2937;
        flex-shrink: 0;
      }

      #live-chat-btn > span:not(#lc-badge) {
        color: #1f2937;
        font-size: 15px;
        font-weight: 600;
        white-space: nowrap;
        letter-spacing: 0.3px;
      }

      #live-chat-btn.open {
        display: none;
      }

      /* ===== Notification Badge ===== */
      #lc-badge {
        position: absolute;
        top: -5px;
        right: -5px;
        width: 24px;
        height: 24px;
        background: #ef4444;
        color: white;
        border-radius: 50%;
        font-size: 12px;
        font-weight: 700;
        display: none;
        align-items: center;
        justify-content: center;
        border: 2px solid white;
        animation: badgePulse 1.5s infinite;
      }

      @keyframes badgePulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }

      /* ===== Chat Box - FULLSCREEN ON MOBILE ===== */
      #live-chat-box {
        position: fixed;
        bottom: 90px;
        right: 24px;
        width: 400px;
        height: 580px;
        max-height: calc(100vh - 120px);
        background: white;
        border-radius: 20px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        display: none;
        flex-direction: column;
        overflow: hidden;
      }

      #live-chat-box.open {
        display: flex;
        animation: slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      }

      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px) scale(0.9);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      /* ===== Header ===== */
      .lc-header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 18px 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
      }

      .lc-header h3 {
        margin: 0;
        font-size: 17px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .lc-header-icon {
        width: 36px;
        height: 36px;
        background: rgba(255,255,255,0.2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
      }

      .lc-header-actions {
        display: flex;
        gap: 8px;
      }

      .lc-new-chat-btn, .lc-close {
        background: rgba(255,255,255,0.2);
        border: none;
        color: white;
        height: 36px;
        border-radius: 18px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        padding: 0 14px;
        gap: 6px;
      }

      .lc-close {
        width: 36px;
        padding: 0;
        font-size: 22px;
      }

      .lc-new-chat-btn:hover, .lc-close:hover {
        background: rgba(255,255,255,0.3);
      }

      .lc-close:hover {
        transform: rotate(90deg);
      }

      /* ===== Form ===== */
      .lc-form {
        padding: 24px;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 18px;
        overflow-y: auto;
      }

      .lc-form-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .lc-form label {
        font-size: 13px;
        font-weight: 600;
        color: #4b5563;
        letter-spacing: 0.3px;
      }

      .lc-form input,
      .lc-form select {
        width: 100%;
        padding: 14px 18px;
        border: 2px solid #e5e7eb;
        border-radius: 12px;
        font-size: 15px;
        font-family: inherit;
        transition: all 0.3s;
        background: #f9fafb;
        box-sizing: border-box;
      }

      .lc-form input:focus,
      .lc-form select:focus {
        outline: none;
        border-color: #667eea;
        background: white;
        box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
      }

      .lc-start-btn {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border: none;
        padding: 16px 24px;
        border-radius: 12px;
        font-size: 16px;
        font-weight: 600;
        font-family: inherit;
        cursor: pointer;
        transition: all 0.3s;
        margin-top: 8px;
      }

      .lc-start-btn:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
      }

      .lc-start-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }

      /* ===== Content Container ===== */
      #lc-content {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }

      /* ===== Messages - PROPER SCROLL ===== */
      .lc-messages {
        flex: 1;
        padding: 20px;
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        gap: 14px;
        background: linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%);
        min-height: 0;
      }

      .lc-msg {
        max-width: 85%;
        padding: 14px 18px;
        border-radius: 18px;
        font-size: 14px;
        line-height: 1.6;
        word-wrap: break-word;
        white-space: pre-wrap;
        animation: msgIn 0.3s ease;
      }

      @keyframes msgIn {
        from {
          opacity: 0;
          transform: translateY(10px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      .lc-msg.customer {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        align-self: flex-end;
        border-bottom-right-radius: 6px;
      }

      .lc-msg.agent {
        background: white;
        color: #1f2937;
        align-self: flex-start;
        border-bottom-left-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }

      .lc-msg img {
        max-width: 100%;
        border-radius: 10px;
        margin-top: 10px;
      }

      .lc-msg-time {
        font-size: 11px;
        opacity: 0.7;
        margin-top: 8px;
      }

      /* ===== Input Area ===== */
      .lc-input-area {
        padding: 16px 20px;
        background: white;
        border-top: 1px solid #e5e7eb;
        flex-shrink: 0;
      }

      .lc-file-preview {
        display: none;
        padding: 10px 14px;
        background: #f3f4f6;
        border-radius: 10px;
        margin-bottom: 12px;
        align-items: center;
        gap: 12px;
      }

      .lc-file-preview.show {
        display: flex;
      }

      .lc-file-preview img {
        max-width: 50px;
        max-height: 50px;
        border-radius: 6px;
        object-fit: cover;
      }

      .lc-file-preview .file-name {
        flex: 1;
        font-size: 13px;
        color: #4b5563;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .lc-file-preview .file-remove {
        padding: 6px 12px;
        background: #ef4444;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 12px;
        cursor: pointer;
      }

      .lc-input-row {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .lc-attach-btn {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: #f3f4f6;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        transition: all 0.2s;
        flex-shrink: 0;
      }

      .lc-attach-btn:hover {
        background: #e5e7eb;
        transform: scale(1.05);
      }

      .lc-input-area input[type="text"] {
        flex: 1;
        padding: 14px 20px;
        border: 2px solid #e5e7eb;
        border-radius: 25px;
        font-size: 14px;
        font-family: inherit;
        transition: all 0.3s;
      }

      .lc-input-area input[type="text"]:focus {
        outline: none;
        border-color: #667eea;
      }

      .lc-send-btn {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        flex-shrink: 0;
      }

      .lc-send-btn:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
      }

      .lc-send-btn svg {
        width: 22px;
        height: 22px;
        fill: white;
        margin-left: 2px;
      }

      .lc-product-badge {
        background: linear-gradient(135deg, #f0f4ff 0%, #e8e0ff 100%);
        padding: 12px 16px;
        font-size: 13px;
        font-weight: 500;
        color: #5b21b6;
        text-align: center;
        flex-shrink: 0;
      }

      /* ===== MOBILE FULLSCREEN ===== */
      @media (max-width: 500px) {
        #live-chat-widget {
          bottom: 16px;
          right: 16px;
        }

        #live-chat-btn span {
          display: none;
        }

        #live-chat-btn {
          width: 60px;
          height: 60px;
          padding: 18px;
          border-radius: 50%;
          justify-content: center;
        }

        #live-chat-box {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          max-height: 100%;
          border-radius: 0;
        }

        #live-chat-box.open {
          animation: mobileSlideUp 0.3s ease;
        }

        @keyframes mobileSlideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        .lc-header {
          padding: 16px 20px;
          padding-top: env(safe-area-inset-top, 16px);
        }

        .lc-messages {
          padding: 16px;
        }
      }

      /* ===== Scrollbar ===== */
      .lc-messages::-webkit-scrollbar {
        width: 6px;
      }

      .lc-messages::-webkit-scrollbar-track {
        background: transparent;
      }

      .lc-messages::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 3px;
      }

      .lc-messages::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
      }

      .lc-form::-webkit-scrollbar {
        width: 4px;
      }

      .lc-form::-webkit-scrollbar-thumb {
        background: #e5e7eb;
        border-radius: 2px;
      }
    `;
        document.head.appendChild(style);

        const widget = document.createElement('div');
        widget.id = 'live-chat-widget';
        widget.innerHTML = `
      <div id="live-chat-box">
        <div class="lc-header">
          <h3>
            <span class="lc-header-icon">💬</span>
            Chat with Us
          </h3>
          <div class="lc-header-actions">
            <button class="lc-new-chat-btn" id="lc-new-chat" style="display:none" onclick="window.LiveChat.newChat()">
              🔄 New Chat
            </button>
            <button class="lc-close" onclick="window.LiveChat.toggle()">×</button>
          </div>
        </div>
        <div id="lc-content"></div>
      </div>
      <button id="live-chat-btn" onclick="window.LiveChat.toggle()">
        <span id="lc-badge">0</span>
        <svg viewBox="0 0 24 24"><path d="M12 3c5.5 0 10 3.58 10 8s-4.5 8-10 8c-1.24 0-2.43-.18-3.53-.5C5.55 21 2 21 2 21c2.33-2.33 2.7-3.9 2.75-4.5C3.05 15.07 2 13.13 2 11c0-4.42 4.5-8 10-8z"/></svg>
        <span>Chat with Us</span>
      </button>
    `;
        document.body.appendChild(widget);

        // Check for existing session
        const hasSession = loadSession();
        if (hasSession) {
            renderChat();
            loadMessages();
            subscribeToMessages();
        } else {
            renderForm();
        }
    }

    function renderForm() {
        document.getElementById('lc-new-chat').style.display = 'none';
        const productOptions = PRODUCTS.map(p => `<option value="${p.id}">${p.name}</option>`).join('');

        document.getElementById('lc-content').innerHTML = `
      <div class="lc-form">
        <div class="lc-form-group">
          <label>👤 Your Name</label>
          <input type="text" id="lc-name" placeholder="Enter your full name" value="${state.customerName}">
        </div>
        
        <div class="lc-form-group">
          <label>📧 Your Email</label>
          <input type="email" id="lc-email" placeholder="Enter your email address" value="${state.customerEmail}">
        </div>
        
        <div class="lc-form-group">
          <label>📦 Product</label>
          <select id="lc-product">
            <option value="">Select a product...</option>
            ${productOptions}
          </select>
        </div>
        
        <button class="lc-start-btn" id="lc-start">
          🚀 Start Conversation
        </button>
      </div>
    `;

        document.getElementById('lc-start').addEventListener('click', startChat);
    }

    function renderChat() {
        document.getElementById('lc-new-chat').style.display = 'flex';
        const product = PRODUCTS.find(p => p.id === state.selectedProduct);

        document.getElementById('lc-content').innerHTML = `
      <div class="lc-product-badge">📦 ${product ? product.name : 'Product Support'}</div>
      <div class="lc-messages" id="lc-messages"></div>
      <div class="lc-input-area">
        <div class="lc-file-preview" id="lc-file-preview">
          <img id="lc-file-thumb" src="" alt="Preview">
          <span class="file-name" id="lc-file-name">filename.jpg</span>
          <button class="file-remove" onclick="window.LiveChat.clearFile()">Remove</button>
        </div>
        <div class="lc-input-row">
          <button class="lc-attach-btn" onclick="document.getElementById('lc-file-input').click()">📎</button>
          <input type="file" id="lc-file-input" accept="image/*,.pdf,.doc,.docx" style="display:none" onchange="window.LiveChat.handleFile(this)">
          <input type="text" id="lc-input" placeholder="Type your message..." onkeypress="if(event.key==='Enter')window.LiveChat.send()">
          <button class="lc-send-btn" onclick="window.LiveChat.send()">
            <svg viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
          </button>
        </div>
      </div>
    `;

        renderMessages();
    }

    function renderMessages() {
        const container = document.getElementById('lc-messages');
        if (!container) return;

        container.innerHTML = state.messages.map(m => {
            let content = escapeHtml(m.message);

            if (m.attachment_url) {
                if (m.attachment_url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
                    content += `<img src="${m.attachment_url}" alt="Attachment" loading="lazy">`;
                } else {
                    content += `<div style="margin-top:8px"><a href="${m.attachment_url}" target="_blank" style="color:inherit;text-decoration:underline;">📎 View Attachment</a></div>`;
                }
            }

            const time = new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            return `
        <div class="lc-msg ${m.sender_type}">
          <div>${content}</div>
          <div class="lc-msg-time">${time}</div>
        </div>
      `;
        }).join('');

        // Scroll to bottom
        container.scrollTop = container.scrollHeight;
    }

    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    async function startChat() {
        const name = document.getElementById('lc-name').value.trim();
        const email = document.getElementById('lc-email').value.trim();
        const product = document.getElementById('lc-product').value;

        if (!name || !email || !product) {
            alert('Please fill in all fields');
            return;
        }

        if (!email.includes('@') || !email.includes('.')) {
            alert('Please enter a valid email address');
            return;
        }

        state.customerName = name;
        state.customerEmail = email;
        state.selectedProduct = product;

        const btn = document.getElementById('lc-start');
        btn.disabled = true;
        btn.textContent = '⏳ Starting...';

        const conversation = await createConversation();
        if (conversation) {
            state.conversationId = conversation.id;
            state.step = 'chat';
            saveSession();
            renderChat();
            subscribeToMessages();

            state.messages.push({
                id: 'welcome',
                sender_type: 'agent',
                message: 'Hi there! 👋 How can I help you today?',
                created_at: new Date().toISOString()
            });
            renderMessages();
        } else {
            btn.disabled = false;
            btn.textContent = '🚀 Start Conversation';
            alert('Failed to start chat. Please try again.');
        }
    }

    async function send() {
        const input = document.getElementById('lc-input');
        const text = input.value.trim();
        const file = state.selectedFile;

        if (!text && !file) return;

        input.value = '';
        let attachmentUrl = null;

        if (file) {
            attachmentUrl = await uploadFile(file);
            clearFile();
        }

        const msg = await sendMessage(text, attachmentUrl);
        if (msg && !state.messages.find(m => m.id === msg.id)) {
            state.messages.push(msg);
            renderMessages();
        }
    }

    function handleFile(input) {
        const file = input.files[0];
        if (!file) return;

        state.selectedFile = file;

        const preview = document.getElementById('lc-file-preview');
        const thumb = document.getElementById('lc-file-thumb');
        const nameEl = document.getElementById('lc-file-name');

        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                thumb.src = e.target.result;
                thumb.style.display = 'block';
            };
            reader.readAsDataURL(file);
        } else {
            thumb.style.display = 'none';
        }

        nameEl.textContent = file.name;
        preview.classList.add('show');
    }

    function clearFile() {
        state.selectedFile = null;
        const preview = document.getElementById('lc-file-preview');
        const fileInput = document.getElementById('lc-file-input');
        if (preview) preview.classList.remove('show');
        if (fileInput) fileInput.value = '';
    }

    function newChat() {
        if (confirm('Start a new conversation? Your current chat will be saved.')) {
            clearSession();
            renderForm();
        }
    }

    function toggle() {
        state.isOpen = !state.isOpen;
        const box = document.getElementById('live-chat-box');
        const btn = document.getElementById('live-chat-btn');

        box.classList.toggle('open', state.isOpen);
        btn.classList.toggle('open', state.isOpen);

        // Clear unread when opening
        if (state.isOpen) {
            state.unreadCount = 0;
            updateBadge();
        }
    }

    // ========== Initialize ==========
    function init() {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2';
        script.onload = () => {
            initSupabase();
            createWidget();
        };
        document.head.appendChild(script);
    }

    window.LiveChat = { toggle, send, handleFile, clearFile, newChat };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
